import { Ref, computed, defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, maxLength, requiredIf, minValue } from "@vuelidate/validators";
import { mapState, mapActions } from "pinia";
import { Container } from "aurelia-dependency-injection";

import { enumToMap } from "../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import {
    ApplicationEnum,
    Ethnicities,
    EthnicityPalliative,
    GenderIdentities,
    MaritalStatuses,
    RacesPalliative,
    SexualOrientation,
    Veterans,
    Gender,
    PhoneTypeEnum
} from "../../../../../../enums/enums";
import { BranchesService } from "../../../../../../services/branches-service";
import { IPhoneNumber } from "../../../../../../resources-vue/vue-interfaces/i-phone-number";
import { Address } from "../../../../../../resources-vue/vue-models/address";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { IGetPatientTag } from "../../../../../../resources-vue/vue-interfaces/i-providers-service";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { ProvidersService } from "../../../../../../services/providers-service";
import { IPatientTags } from "./../../../../../../resources-vue/vue-interfaces/i-patient-tags";
import { LookupService } from "../../../../../../services/lookup-service";
import { Interpreter } from "../../../../../../enums/PalConvertToPendingInterpreter";
import { ILanguagesResponsePal } from "../../../../../../resources-vue/vue-interfaces/i-lookup";

import ValidateAddress from "../../../../../../resources-vue/vue-dialogs/ValidateAddress/ValidateAddress.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import MultiSelectDropdown from "../../../../../../resources-vue/vue-custom-elements/MultiSelectDropdown/MultiSelectDropdown.vue";
import PhoneNumber from "../../../../../../resources-vue/vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import TagsInput from "../../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import AddressForm from "../../AddressForm/AddressForm.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        MultiSelectDropdown,
        PhoneNumber,
        TagsInput,
        AddressForm,
        TypeaheadInput
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef),
            v: computed(() => this.v$)
        };
    },

    validations() {
        return {
            patientDetails: {
                demographics: {
                    medicalRecordNumber: { required },
                    firstName: { required },
                    lastName: { required },
                    dateOfBirth: { required },
                    gender: { minValue: minValue(1) },
                    ethnicityOnly: {
                        required: requiredIf(() => {
                            return this.isEthnicityDeclined === false;
                        })
                    },
                    primaryLanguage: {
                        required: requiredIf(() => {
                            return !this.patientDetails.demographics.declineToSpecifyLanguage;
                        })
                    },
                    otherGenderIdentity: {
                        required: requiredIf(() => {
                            return this.patientDetails.demographics.genderIdentity === 6;
                        })
                    },
                    otherSexualOrientation: {
                        required: requiredIf(() => {
                            return this.patientDetails.demographics.sexualOrientation === 4;
                        })
                    },
                    phoneNumbers: [
                        {
                            number: { required },
                            type: { minValue: minValue(1) }
                        }
                    ],
                    locationId: { required },
                    socialSecurityNumber: { minLength: minLength(9), maxLength: maxLength(9) }
                }
            }
        };
    },

    data(vm) {
        return {
            dialogRef: null,
            _branchesService: null,
            _providersService: null,
            _lookupService: null,
            _visitAddressCache: null,
            _visitAddressFacilityCache: null,
            _mailingAddressCache: null,
            _mailingAddressFacilityCache: null,
            mailingAddrSameAsPrimaryAddr: false,
            visitAddrSameAsPrimaryAddr: true,
            isVeteranSelected: false,
            selectedRaces: [],
            isEthnicityDeclined: false,
            isRacesDeclined: false,
            branches: null,
            phoneNumberTypeOptionsConfig: [PhoneTypeEnum.Home, PhoneTypeEnum.Mobile, PhoneTypeEnum.Facility],
            isAddressValidationInProgress: false,
            patientDetailsOptionsAddressFormRef: null,
            Genders: Array.from(Gender.enumWithDescriptions.values()),
            MaritalStatuses: enumToMap(MaritalStatuses),
            Ethnicity: enumToMap(EthnicityPalliative),
            Races: Array.from(RacesPalliative.enumWithDescriptions.values()),
            Veterans: Array.from(Veterans.enumWithDescriptions.values()),
            SexualOrientation: enumToMap(SexualOrientation),
            GenderIdentities: enumToMap(GenderIdentities),
            interpreters: Array.from(Interpreter.enumWithDescriptions.values()),
            genderUnknownValue: Gender.enum.Unknown,
            selectedTags: [] as ITypeaheadOptions[],
            patientTags: [] as IEnumResponse[],
            selectedLanguage: null,
            selectedOtherLanguages: [],
            showAllByDefault: true,
            languagesList: [],
            formatDateInput,
            enumToMap,
            v$: useVuelidate(),
            tooltip:
                "For emergency preparedness, patient will be contacted at this number. This will also populate to the emergency preparedness report."
        };
    },
    created() {
        this._branchesService = Container.instance.get(BranchesService);
        this._providersService = Container.instance.get(ProvidersService);
        this._lookupService = Container.instance.get(LookupService);
    },
    async mounted() {
        await this.initOptions();
        this.initFields();
        this.getTags();
        this.getLanguagesList();
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["patientDetails", "accountId", "application", "errors"]),
        veteranOptions() {
            return this.Veterans?.map((option) => ({
                name: option.name,
                value: option.value
            }));
        },
        primaryNumber: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.number;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].number = value;
            }
        },
        primaryExtension: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.extension;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].extension = value;
            }
        },
        primaryType: {
            get() {
                const value =
                    this.patientDetails.demographics.phoneNumbers?.[
                        this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                    ]?.type;
                return value;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].type = value;
            }
        },
        primaryCountry: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.country;
            },
            set(value: any) {
                return (this.patientDetails.demographics.phoneNumbers[
                    this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].country = value);
            }
        },
        alternateNumber: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.number;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].number = value;
            }
        },
        alternateExtension: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.extension;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].extension = value;
            }
        },
        alternateType: {
            get() {
                const value =
                    this.patientDetails.demographics.phoneNumbers?.[
                        this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                    ]?.type;
                return value;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].type = value;
            }
        },
        alternateCountry: {
            get() {
                return this.patientDetails.demographics.phoneNumbers?.[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ]?.country;
            },
            set(value: any) {
                this.patientDetails.demographics.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers)
                ].country = value;
            }
        },
        isUnitedStates() {
            return this.patientDetails.demographics.primaryAddress.country === "840";
        }
    },
    watch: {
        mailingAddrSameAsPrimaryAddr(newValue: boolean) {
            this.mailingAddrSameAsPrimaryAddrChanged(newValue);
        },
        visitAddrSameAsPrimaryAddr(newValue: boolean) {
            this.visitAddrSameAsPrimaryAddrChanged(newValue);
        },
        isVeteranSelected(newValue: boolean) {
            if (!!this.patientDetails.demographics && !newValue) {
                this.patientDetails.demographics.veteran = [];
            }
        },
        selectedRaces() {
            if (!!this.selectedRaces && !!this.patientDetails.demographics) {
                this.patientDetails.demographics.race = this.selectedRaces.map((race) => Number(race.value));
            }
        },
        selectedTags() {
            if (!!this.patientDetails) {
                this.patientDetails.demographics.tagIds = this.selectedTags?.map((tags) => tags.value);
            }
        },
        languagesList: {
            handler() {
                this.selectedLanguage = this.languagesList.find(
                    (lang) => lang.value === this.patientDetails.demographics?.primaryLanguage
                );
            },
            deep: true
        }
    },
    methods: {
        ...mapActions(useConvertToPendingPal, {
            setErrors: "SET_NEW_ERRORS"
        }),
        async initOptions() {
            const branches = await this._branchesService.getAllBranches();
            this.branches = branches.filter((branch: any) => branch.accountId == this.accountId);
        },
        initFields() {
            if (!!this.patientDetails.demographics) {
                if (!this.patientDetails.demographics.previousAddress) {
                    this.patientDetails.demographics.previousAddress = new Address();
                } else {
                    this.patientDetails.demographics.hasPreviousAddress =
                        !!this.patientDetails.demographics.previousAddress.addressLine1;
                }
                this._mailingAddressCache = new Address(this.patientDetails.demographics.mailingAddress);
                this._mailingAddressFacilityCache = this.patientDetails.demographics.mailingAddressFacilityTypeId;
                this.mailingAddrSameAsPrimaryAddr = this.patientDetails.demographics.isMailingAddressSameAsPrimary;
                this._visitAddressCache = new Address(this.patientDetails.demographics.visitAddress);
                this._visitAddressFacilityCache = this.patientDetails.demographics.visitAddressFacilityTypeId;

                if (
                    this.patientDetails.demographics.isVisitAddressSameAsPrimary !== undefined &&
                    this.patientDetails.demographics.isVisitAddressSameAsPrimary !== null
                ) {
                    this.visitAddrSameAsPrimaryAddr = this.patientDetails.demographics.isVisitAddressSameAsPrimary;
                } else {
                    this.visitAddrSameAsPrimaryAddr =
                        this.patientDetails.demographics.visitAddress?.addressLine1 ===
                        this.patientDetails.demographics.primaryAddress?.addressLine1;
                }

                if (this.primaryNumberIndex(this.patientDetails.demographics.phoneNumbers) < 0) {
                    this.patientDetails.demographics.phoneNumbers =
                        this.patientDetails.demographics.phoneNumbers.concat({
                            number: "",
                            extension: "",
                            type: 1,
                            country: null,
                            isPrimary: true
                        });
                }
                if (this.alternativeNumberIndex(this.patientDetails.demographics.phoneNumbers) < 0) {
                    this.patientDetails.demographics.phoneNumbers =
                        this.patientDetails.demographics.phoneNumbers.concat({
                            number: "",
                            extension: "",
                            type: 1,
                            country: null,
                            isPrimary: false
                        });
                }

                this.selectedRaces = this.Races.filter((race) => {
                    return this.patientDetails.demographics.race?.some((item: any) => {
                        return race.value == item;
                    });
                }) as any;

                if (this.patientDetails.demographics.veteran?.length > 0) {
                    this.isVeteranSelected = true;
                }
                this.isEthnicityDeclined = this.patientDetails.demographics.declineToSpecifyEthnicity;
                this.isRacesDeclined = this.patientDetails.demographics.declineToSpecifyRace;
            }
        },
        hasPreviousAddressChanged() {
            if (!!this.patientDetails.demographics && !this.patientDetails.demographics.hasPreviousAddress) {
                this.patientDetails.demographics.previousAddress = new Address();
                this.patientDetails.demographics.previousAddressFacilityTypeId = null;
            }
        },
        handlePrimaryAddressChange() {
            if (this.mailingAddrSameAsPrimaryAddr) {
                this.patientDetails.demographics.mailingAddress = new Address(
                    this.patientDetails.demographics.primaryAddress
                );
                this.patientDetails.demographics.mailingAddressFacilityTypeId =
                    this.patientDetails.demographics.primaryAddressFacilityTypeId;
            }
            if (this.visitAddrSameAsPrimaryAddr) {
                this.patientDetails.demographics.visitAddress = new Address(
                    this.patientDetails.demographics.primaryAddress
                );
                this.patientDetails.demographics.visitAddressFacilityTypeId =
                    this.patientDetails.demographics.primaryAddressFacilityTypeId;
            }
        },

        resetSameAsPrimaryAddress() {
            if (this.patientDetails.demographics.isVisitAddressSameAsPrimary) {
                this.visitAddrSameAsPrimaryAddrChanged(true);
            }
            if (this.patientDetails.demographics.isMailingAddressSameAsPrimary) {
                this.mailingAddrSameAsPrimaryAddrChanged(true);
            }
        },
        mailingAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.patientDetails.demographics) {
                if (newValue === true) {
                    this.patientDetails.demographics.isMailingAddressSameAsPrimary = true;
                    this.patientDetails.demographics.mailingAddress = new Address(
                        this.patientDetails.demographics.primaryAddress
                    );
                    this.patientDetails.demographics.mailingAddressFacilityTypeId =
                        this.patientDetails.demographics.primaryAddressFacilityTypeId;
                } else {
                    this.patientDetails.demographics.isMailingAddressSameAsPrimary = false;
                    if (this._mailingAddressCache) {
                        this.patientDetails.demographics.mailingAddress = new Address(this._mailingAddressCache);
                        this.patientDetails.demographics.mailingAddressFacilityTypeId =
                            this._mailingAddressFacilityCache;
                    } else {
                        this.patientDetails.demographics.mailingAddress = new Address();
                        this.patientDetails.demographics.mailingAddressFacilityTypeId = null;
                    }
                }
            }
        },
        visitAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.patientDetails.demographics) {
                if (newValue === true) {
                    this.patientDetails.demographics.isVisitAddressSameAsPrimary = true;
                    this.patientDetails.demographics.visitAddress = new Address(
                        this.patientDetails.demographics.primaryAddress
                    );
                    this.patientDetails.demographics.visitAddressFacilityTypeId =
                        this.patientDetails.demographics.primaryAddressFacilityTypeId;
                } else {
                    this.patientDetails.demographics.isVisitAddressSameAsPrimary = false;
                    if (this._visitAddressCache) {
                        this.patientDetails.demographics.visitAddress = new Address(this._visitAddressCache);
                        this.patientDetails.demographics.visitAddressFacilityTypeId = this._visitAddressFacilityCache;
                    } else {
                        this.patientDetails.demographics.visitAddress = new Address();
                        this.patientDetails.demographics.visitAddressFacilityTypeId = null;
                    }
                }
            }
        },

        primaryNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (!!phoneNumbers) {
                return phoneNumbers.findIndex((e) => e.isPrimary);
            }
            return 0;
        },
        alternativeNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (!!phoneNumbers) {
                return phoneNumbers.findIndex((e) => !e.isPrimary);
            }
            return 0;
        },

        ethnicityDeclined() {
            if (this.isEthnicityDeclined) {
                this.patientDetails.demographics.ethnicity = [];
            }
            if (!!this.patientDetails.demographics) {
                this.patientDetails.demographics.declineToSpecifyEthnicity = this.isEthnicityDeclined;
            }
        },
        racesDeclined() {
            if (this.isRacesDeclined) {
                this.patientDetails.demographics.race = [];
                this.selectedRaces = [];
            }
            if (!!this.patientDetails.demographics) {
                this.patientDetails.demographics.declineToSpecifyRace = this.isRacesDeclined;
            }
        },

        onDateChange(date: any, name: string) {
            if (name === "dateOfBirth") this.patientDetails.demographics.dateOfBirth = formatDateInput(date);
            if (date.length) {
                if ([...new Set(this.errors.demographics)].includes(name)) {
                    const newErrors = [...new Set(this.errors.demographics)].filter((val: any) => val !== name);
                    const payload = {
                        property: "demographics",
                        value: newErrors
                    };
                    this.setErrors(payload, true, true);
                }
            }
        },
        handleDateInputChange(e: any) {
            this.patientDetails.demographics.dateOfBirth = e.target.value;
            this.handleAllInput(e, "dateOfBirth");
        },
        getDate(date: any, name: string) {
            if (name === "dateOfBirth") {
                this.patientDetails.demographics.dateOfBirth = date;
            }
            this.handleAllInput(date, "dateOfBirth");
        },
        handleVeteran() {
            this.isVeteranSelected = !this.isVeteranSelected;
        },
        selectedItemsUpdated(value: any) {
            if (this.patientDetails.demographics.veteran.includes(value)) {
                const index = this.patientDetails.demographics.veteran.indexOf(value);
                this.patientDetails.demographics.veteran.splice(index, 1);
                return;
            }
            this.patientDetails.demographics.veteran.push(value);
        },
        updateSelectedRaces(value: any) {
            this.selectedRaces = value;
        },
        async validateAddress() {
            let model = {
                originalAddress: {
                    addressLine1: this.patientDetails.demographics.primaryAddress.addressLine1,
                    addressLine2:
                        this.patientDetails.demographics.primaryAddress.addressLine2 == null
                            ? ""
                            : this.patientDetails.demographics.primaryAddress.addressLine2,
                    city: this.patientDetails.demographics.primaryAddress.city,
                    state: this.patientDetails.demographics.primaryAddress.state,
                    zipCode: this.patientDetails.demographics.primaryAddress.zipCode,
                    county: this.patientDetails.demographics.primaryAddress.county,
                    country: this.patientDetails.demographics.primaryAddress.country,
                    latitude:
                        this.patientDetails.demographics.primaryAddress.latitude == null
                            ? ""
                            : this.patientDetails.demographics.primaryAddress.latitude,
                    longitude:
                        this.patientDetails.demographics.primaryAddress.longitude == null
                            ? ""
                            : this.patientDetails.demographics.primaryAddress.longitude
                }
            };
            this.isAddressValidationInProgress = true;
            let updatedAddress = null;
            this.dialogRef = this.$dialog.open(ValidateAddress, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    model
                },
                onClose: (options) => {
                    if (options.data?.success) {
                        updatedAddress = options.data.updatedAddress;
                        this.patientDetails.demographics.primaryAddress.addressLine1 = updatedAddress.addressLine1;
                        this.patientDetails.demographics.primaryAddress.addressLine2 = updatedAddress.addressLine2;
                        this.patientDetails.demographics.primaryAddress.city = updatedAddress.city;
                        this.patientDetails.demographics.primaryAddress.state = updatedAddress.state;
                        this.patientDetails.demographics.primaryAddress.zipCode = updatedAddress.zipCode;
                        this.patientDetails.demographics.primaryAddress.county = updatedAddress.county;
                        this.patientDetails.demographics.primaryAddress.latitude = updatedAddress.latitude;
                        this.patientDetails.demographics.primaryAddress.longitude = updatedAddress.longitude;
                        this.patientDetails.demographics.primaryAddress.isValidated = true;
                        this.patientDetails.demographics.primaryAddress.isBypassed = false;
                        if (updatedAddress.country) {
                            this.patientDetails.demographics.primaryAddress.country = updatedAddress.country;
                        }
                    } else {
                        this.patientDetails.demographics.primaryAddress.isValidated = false;
                        this.patientDetails.demographics.primaryAddress.isBypassed = true;
                    }
                }
            });
            this.isAddressValidationInProgress = false;
        },

        async getTags() {
            const patientTagsQuery: IPatientTags = {
                isActive: true,
                application: this.application,
                accountId: this.accountId as string
            };

            let patientTags = await this._providersService.getActivePatientTags(patientTagsQuery);
            this.patientTags = patientTags.map((tag: IGetPatientTag) => ({
                name: tag.name,
                value: tag.id
            }));

            this.selectedTags = this.patientDetails.demographics.tags?.map((tag: any) => ({
                name: tag.name,
                value: tag.id
            }));
        },
        updateTags(newValue: any) {
            this.selectedTags = newValue;
        },

        async getLanguagesList() {
            const data = await this._lookupService.getLanguagesPal({
                application: this.application
            });
            this.languagesList = data.map((language: ILanguagesResponsePal) => ({
                name: language.Name,
                value: language.Value
            }));
        },

        getLanguagesFilter(filter: string) {
            if (filter) {
                const languagesFilter = this.languagesList.filter((languages) => {
                    return languages?.name.toLowerCase().includes(filter.toLowerCase());
                });
                return languagesFilter;
            } else {
                return this.languagesList;
            }
        },

        updateLanguage(data: any) {
            if (data) {
                this.selectedLanguage = data;
                this.patientDetails.demographics.primaryLanguage = data.value;
            } else {
                this.selectedLanguage = null;
                this.patientDetails.demographics.primaryLanguage = "";
            }
        },

        handleDeclineLanguage() {
            this.patientDetails.demographics.declineToSpecifyLanguage =
                !this.patientDetails.demographics.declineToSpecifyLanguage;
            if (this.patientDetails.demographics.declineToSpecifyLanguage) {
                this.patientDetails.demographics.primaryLanguage = "";
            }
        },

        updateSelectedOtherLanguages(data: any) {
            this.selectedOtherLanguages = data;
            this.patientDetails.demographics.languages = this.selectedOtherLanguages.map((language) => language.value);
        },

        handleAllInput(e: any, field: string) {
            const value = e.target.value;
            if (value.length) {
                if ([...new Set(this.errors.demographics)].includes(field)) {
                    const newErrors = [...new Set(this.errors.demographics)].filter((val: any) => val !== field);
                    const payload = {
                        property: "demographics",
                        value: newErrors
                    };
                    this.setErrors(payload, true, true);
                }
            }
        }
    }
});
